import { Suspense, lazy, useEffect } from "react";
import { Container, Col, Row } from "reactstrap";
import { Fields } from "@repoflow/components/fields";
import { useSectionContext } from "@repoflow/components/section";
import { Link } from "react-router-dom";
import { List } from "@repoflow/components/list-cursor";

import * as BucketFileQL from "queries/bucket-file";
import { ApolloConsumer } from "@apollo/client";
import { FileRender } from "components/file";
import * as Loading from "@repoflow/components/loading";
import { useInterval } from "@repoflow/components/interval";

import { LimitsSummary } from "../statistics/limits";

// recharts is ~100KB gzipped. Lazily loaded here and on the statistics page so
// the bucket LIST — which links to neither — never pays for it.
const CaptureCharts = lazy(() => import("../statistics/charts"));

export const Sidebar = () => null;

export const Indice = () => null;

export const Content = (props) => (
  <ApolloConsumer>
    {(client) => <ContentHandler {...props} client={client} />}
  </ApolloConsumer>
);

const ContentHandler = ({
  client,
  match: {
    params,
    params: { bucketId },
  },
}) => {
  const { values, schema, base } = useSectionContext();

  return (
    <>
      <Container className="mt-4">
        <Row>
          <Col>
            <div className="float-right">
              <Link className="btn btn-primary" to={`${base}/edit`}>
                Edit
              </Link>{" "}
              <Link className="btn btn-danger" to={`${base}/remove`}>
                Remove
              </Link>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Fields
              schema={schema}
              values={values}
              target={{ $ref: "/Public/Bucket" }}
              config={{
                "/Public/Bucket": {
                  fields: ["size", "exts", "minFileSize", "maxFileSize"],
                },
              }}
            />
          </Col>
        </Row>
      </Container>

      <Container>
        <div className="mt-4"></div>

        <Row>
          <Col>
            <Link className={"btn btn-primary"} to={`${base}/file/new`}>
              <i className="fa fa-upload" /> Upload
            </Link>
          </Col>
        </Row>
        <div className="mt-4"></div>
        <Row>
          <Col>
            <List
              path={"viewer.account.buckets.get.files"}
              query={BucketFileQL.list}
              variables={params}
              schema={schema}
              target={{ $ref: "/Public/BucketFile" }}
              empty={"No files in this bucket yet."}
              config={{
                "/Public/BucketFile": {
                  fields: [
                    { property: "fileId", widget: "fileViewer" },
                    "status",
                    "tags",
                    "size",
                    "createdAt",
                  ],
                },
              }}
              widgets={{
                fileViewer: {
                  View: (props) => {
                    const { value: fileId, field: { parent: { fields } } } = props
                    const ext = fields.find(({ name }) => name === "ext")
                    return (
                      <>
                        <FileRender
                          bucketId={bucketId}
                          fileId={fileId}
                          ext={ext.value}
                        />
                        <div>{fileId.substr(0, 12)}...</div>
                      </>
                    )
                  },
                },
              }}
              content={RowContent}
              attributes={{ bucketId, client, base }}
            />{" "}
          </Col>
        </Row>
      </Container>

      {/* The summary, and only the summary.

          Two blocks: how full the bucket is, and what it did lately. Every
          chart, the extension breakdown and the row-level capture list live
          under Statistics — this page is the answer to "is anything wrong
          here", not a place to explore from.

          LAST ON THE PAGE, below the files. What this page is opened to do is
          look at the bucket and its contents; the statistics are what you drop
          to when something looks wrong, and they were pushing the file listing
          — the reason the page exists — below the fold. datum's point page is
          arranged the same way, for the same reason.

          No filter argument on the charts. The server defaults to the last 24
          hours, which is the right window for an overview, and the explorer
          with its range picker is one link away. */}
      <Container>
        <div className="mt-4"></div>

        <Row>
          <Col>
            <div className="float-right">
              <Link to={`${base}/statistics`}>Go to Statistics...</Link>
            </div>
            <h5>Statistics</h5>
          </Col>
        </Row>

        <div className="mt-3"></div>

        <Row>
          <Col lg={6}>
            <LimitsSummary bucketId={bucketId} />
          </Col>
          <Col lg={6}>
            {/* Two tiles here, not six — `compact` picks the pair. Six of them
                nested inside a half-width column wrapped a byte figure across
                four lines ("+2 96. 0 KB"), which is what this was changed to
                stop. */}
            <Suspense fallback={<Loading.Content />}>
              <CaptureCharts
                bucketId={bucketId}
                compact
                rangeLabel={"Last 24 hours"}
              />
            </Suspense>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const RowContent = ({
  item: { fileId, status },
  attributes: { bucketId, client, base },
  update,
  refetch
}) => {
  const { active } = useInterval({
    callback: async () => {
      const res = await client.query({
        query: BucketFileQL.get,
        variables: {
          bucketId,
          fileId,
        },
        fetchPolicy: "network-only",
      });

      const file = res.data.viewer.account.buckets.get.files.get;

      if (file) {
        if (update) {
          update(file);
        }
      } else {
        refetch()
      }
    },
    interval: 2500,
  });

  useEffect(() => {
    if (status === "processing") {
      active(true);
    } else {
      active(false);
    }
  }, [status]);

  return status !== "processing" ? (
    <>
      <Link className={"btn btn-primary mt-2"} to={`${base}/file/${fileId}`}>
        View
      </Link>
    </>
  ) : (
    <div>
      <Loading.Content />
    </div>
  );
};
